import { type DeleteLabelMutation } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { deleteLabelMutationDocument } from "./label.gql";
import { EVENT_LABEL_DELETED, trackLabelEvent } from "./tracking";
type DeletedLabel = DeleteLabelMutation["deleteLabel"];

export interface DeleteLabelInput {
  id: string;
}

export interface UseDeleteLabelProps {
  onCompleted?: (data: DeletedLabel) => void;
  onError?: (e: Error) => void;
}

export const useDeleteLabel = ({ onCompleted, onError }: UseDeleteLabelProps) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: DeleteLabelInput) => {
      const client = createOperationClient("deleteLabel");
      const data = await client.request(deleteLabelMutationDocument, { input });

      return data.deleteLabel;
    },
    onSuccess: (label) => {
      // Remove the label from cache
      queryClient.removeQueries({
        queryKey: queryKeys.labels.detail(label.id),
      });

      // Invalidate the list to refetch without the deleted item
      queryClient.invalidateQueries({
        queryKey: queryKeys.labels.list(label.workspaceId),
      });

      // Invalidate parent label group so its labelList updates
      if (label.labelGroupId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.labelGroups.detail(label.labelGroupId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.labelGroups.list(label.workspaceId),
        });
      }

      // Track the event
      trackLabelEvent(EVENT_LABEL_DELETED, label);

      // Call the onCompleted callback
      if (onCompleted) {
        onCompleted(label);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  return {
    deleteLabel: mutation.mutate,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
